import * as queryString from 'querystring';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';

import { Button, FoodTrayTemplateLink, NavigateBackButton, Select, Theme, TopBar } from '../../components';
import { FoodTrayTemplate, FoodTrayTemplateField, GET_TEMPLATE_SORT_OPTIONS, SORT_ORDER } from '../../models';
import { FrontOfficeLink } from '../../models/Routes';
import {
  foodTrayTemplateEditActions,
  foodTrayTemplatesActions,
  foodTrayTemplatesSelectors,
  storeActions,
  storeSelectors,
  userSelectors,
} from '../../redux';

const TemplateSelect = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const templates = useSelector(foodTrayTemplatesSelectors.getFoodTrayTemplates);
  const store = useSelector(storeSelectors.getStore);
  const isTemplatesLoading = useSelector(foodTrayTemplatesSelectors.isLoading);
  const user = useSelector(userSelectors.getUserInfo);

  const [activeStoreId] = useQueryParam('store', StringParam);
  const [activeDepartmentId] = useQueryParam('department', StringParam);
  const [sortField, setSortField] = useQueryParam('sort', StringParam);

  useEffect(() => {
    if (!activeStoreId || !activeDepartmentId) {
      history.replace('/');
    } else {
      const sort = sortField || FoodTrayTemplateField.CREATED_AT;
      const sortOrder = GET_TEMPLATE_SORT_OPTIONS().find(sortOption => sortOption.value === sort)?.sortOrder || SORT_ORDER.DESC;
      dispatch(
        foodTrayTemplatesActions.getFoodTrayTemplates({
          sort: [sort + ',' + sortOrder],
          departmentId: activeDepartmentId,
        }),
      );
    }
  }, [activeStoreId, activeDepartmentId, sortField]);

  useEffect(() => {
    if (user.store && user.store.id !== activeStoreId) {
      history.replace('/');
    }
    if (activeStoreId) {
      dispatch(storeActions.getStore(activeStoreId));
    }
  }, [activeStoreId]);

  useEffect(() => {
    dispatch(foodTrayTemplatesActions.clearFoodTrayTemplate());
  }, []);

  useEffect(() => {
    dispatch(foodTrayTemplateEditActions.resetFoodTrayTemplate(activeDepartmentId));
  }, [activeDepartmentId]);

  useEffect(() => {
    if (activeStoreId) {
      dispatch(storeActions.getStore(activeStoreId));
    }
  }, [activeStoreId]);

  const renderTemplate = (template: FoodTrayTemplate | undefined, index: number) => {
    return (
      <FoodTrayTemplateLink
        template={template}
        link={`/dashboard/${template?.id}`}
        key={'food-tray-template-link-' + template?.id + '-' + index}
      />
    );
  };

  const backLink = `${FrontOfficeLink.DEPARTMENT_SELECT}?${queryString.stringify({ store: activeStoreId })}`;
  return (
    <>
      <TopBar>
        <Button theme={Theme.SECONDARY} onClickCallback={() => history.push(backLink)} icon='Store'>
          {t('lblChangeDepartment')}
        </Button>
        <div className='divider' />
      </TopBar>
      <div className='front-office'>
        <NavigateBackButton label='lblBackToDepartments' link={backLink} />
        <div className='content-container'>
          <h2>{t('titleSelectTemplate')}</h2>
          <h3>{`${store?.name} > ${store?.departments?.find(d => d.id === activeDepartmentId)?.departmentTemplate.name}`}</h3>
        </div>
        <div className='sort-select'>
          <Select label='lblSortBy' value={sortField} onChange={setSortField} options={GET_TEMPLATE_SORT_OPTIONS()} />
        </div>
        <div className='templates'>{(isTemplatesLoading ? [undefined, undefined] : templates).map(renderTemplate)}</div>
      </div>
    </>
  );
};

export default TemplateSelect;
